import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { trackAction } from "./PageAnalytics";
import ShowcaseVisual, { ratioValue } from "./ShowcaseVisual";
import MediaLightbox from "./MediaLightbox";
import VisualLayer, { VisualBackdrop, useWideStage } from "./VisualLayer";

// A project shown through several of its real screens on one stable stage.
//
// It opens paused on the first screen, which has to work on its own. Play is
// an explicit choice; any manual step, swipe, keyboard focus on the stage,
// the gallery leaving view, the tab being hidden or another gallery starting
// stops it, and nothing resumes it except Play. Reduced motion drops Play and
// the crossfade. The controls are laid over the stage but sit beside the
// stage's button, never inside it, and only manual changes are announced.
//
// Phones have no controls on the page (home.css), so there the gallery plays
// by itself while most of it is in view, which shows it is a carousel. The
// dialog pauses it, and a swipe hands it to the reader for good.
//
// The screens open the media dialog, on the slide shown, with only the
// screens in it; the backdrop around them is not clickable. The way into the
// article is the caption's "Read case study".
//
// Screens that fail to load are dropped; with one left the gallery becomes a
// plain stage with no controls. Screens after the first mount only once the
// gallery is near the viewport and the browser is idle. A step moves the
// controls straight away, but the stage keeps the current screen until every
// layer of the next one has loaded, so it never fades to a half-drawn slide.
// One backdrop sits under every slide of a project.
//
// A screen with a clip plays it only while it is the one shown, from its
// start, and a slide's `hold` lets playback stay on it for one pass.

export const GALLERY_INTERVAL_MS = 5500;
const SWIPE_PX = 40;
const PLAY_EVENT = "pf-gallery-play";
const REDUCED_QUERY = "(prefers-reduced-motion: reduce)";
const PHONE_QUERY = "(max-width: 639px)";

function useMediaQuery(media) {
  const [matches, setMatches] = useState(() => window.matchMedia(media).matches);
  useEffect(() => {
    const query = window.matchMedia(media);
    const update = () => setMatches(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, [media]);
  return matches;
}

const useReducedMotion = () => useMediaQuery(REDUCED_QUERY);

const whenIdle = (callback) => {
  if (typeof window.requestIdleCallback === "function") {
    const handle = window.requestIdleCallback(callback, { timeout: 1500 });
    return () => window.cancelIdleCallback(handle);
  }
  const handle = setTimeout(callback, 600);
  return () => clearTimeout(handle);
};

function isKeyboardFocus(node) {
  try {
    return node.matches(":focus-visible");
  } catch (error) {
    return false;
  }
}

// Layers the current arrangement shows; the others are display: none and,
// being lazy, never load, so they cannot hold a slide back.
const shownLayers = (slide, wide) =>
  slide.layers
    .map((layer, index) => ({ layer, index }))
    .filter(({ layer }) => {
      if (wide) return Boolean(layer.wide);
      return Boolean(layer.narrow === undefined ? layer.wide : layer.narrow);
    });

// A slide's layers re-placed on a stage cut to their own bounds, with room
// for a phone's bezel, so the dialog shows the screens without the backdrop.
// A layer's height is in stage-height units (width × crop ratio × stage
// ratio); a browser window adds its title bar above its box (home.css).
export function screensOnly(layers, ratio) {
  const r = ratioValue(ratio);
  const boxes = layers
    .filter((layer) => layer.wide)
    .map((layer) => {
      const [, , cw, ch] = layer.wide.crop;
      const [x, y, w] = layer.wide.at;
      const bar = layer.wide.browser ? w * 0.034 * r : 0;
      return { layer, x, y, w, top: y - bar / 2, bottom: y + bar / 2 + w * (ch / cw) * r };
    });
  const padW = Math.max(...boxes.map((box) => box.w)) * 0.04;
  const padH = padW * r;
  const left = Math.min(...boxes.map((box) => box.x)) - padW;
  const right = Math.max(...boxes.map((box) => box.x + box.w)) + padW;
  const top = Math.min(...boxes.map((box) => box.top)) - padH;
  const bottom = Math.max(...boxes.map((box) => box.bottom)) + padH;
  const bw = right - left;
  const bh = bottom - top;
  return {
    ratio: String((bw / bh) * r),
    layers: boxes.map(({ layer, x, y, w }) => ({
      ...layer,
      narrow: undefined,
      wide: {
        ...layer.wide,
        at: [((x - left) / bw) * 100, ((y - top) / bh) * 100, (w / bw) * 100],
      },
    })),
  };
}

function GalleryStage({ project, gallery, slides, priority, onSlideError }) {
  const wide = useWideStage();
  const reduced = useReducedMotion();
  const phone = useMediaQuery(PHONE_QUERY);
  const [inView, setInView] = useState(false);
  const [handedOver, setHandedOver] = useState(false);
  const regionRef = useRef(null);
  const swipe = useRef(null);
  const suppressClick = useRef(false);
  const id = useId();
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [primed, setPrimed] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [loaded, setLoaded] = useState({});
  const [shownKey, setShownKey] = useState(slides[0].key);
  const [viewerOpen, setViewerOpen] = useState(false);

  const count = slides.length;
  const current = Math.min(index, count - 1);
  const { subject } = gallery;
  const target = slides[current];
  const shown = slides.find((slide) => slide.key === shownKey) || target;
  const hold = target.hold || GALLERY_INTERVAL_MS;

  const markLoaded = useCallback((key, layerIndex) => {
    setLoaded((map) => {
      const list = map[key] || [];
      return list.includes(layerIndex) ? map : { ...map, [key]: [...list, layerIndex] };
    });
  }, []);

  const targetReady = shownLayers(target, wide).every(({ index: layerIndex }) =>
    (loaded[target.key] || []).includes(layerIndex)
  );

  useEffect(() => {
    if (targetReady) setShownKey(target.key);
  }, [targetReady, target.key]);

  const go = useCallback(
    (delta, source) => {
      const next = (current + delta + count) % count;
      setIndex(next);
      setPlaying(false);
      setHandedOver(true);
      setPrimed(true);
      setAnnouncement(`Screen ${next + 1} of ${count}: ${slides[next].label}`);
      trackAction("gallery", `${project.id}:${source}`);
    },
    [count, current, project.id, slides]
  );

  const togglePlay = () => {
    setPrimed(true);
    setAnnouncement("");
    setPlaying((value) => !value);
    trackAction("gallery", `${project.id}:${playing ? "pause" : "play"}`);
  };

  useEffect(() => {
    if (!playing) return undefined;
    const timer = setTimeout(() => setIndex((value) => (value + 1) % count), hold);
    return () => clearTimeout(timer);
  }, [playing, current, count, hold]);

  useEffect(() => {
    if (!playing) return undefined;
    window.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: id }));
    const onOtherPlay = (event) => {
      if (event.detail !== id) setPlaying(false);
    };
    const onVisibility = () => {
      if (document.hidden) setPlaying(false);
    };
    window.addEventListener(PLAY_EVENT, onOtherPlay);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener(PLAY_EVENT, onOtherPlay);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [playing, id]);

  useEffect(() => {
    if (reduced) setPlaying(false);
  }, [reduced]);

  const autoplay = phone && !reduced && !handedOver;

  useEffect(() => {
    const node = regionRef.current;
    if (!autoplay || !node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.intersectionRatio >= 0.6),
      { threshold: [0, 0.6] }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [autoplay]);

  useEffect(() => {
    if (!autoplay) return;
    setPlaying(inView && !viewerOpen);
    if (inView) setPrimed(true);
  }, [autoplay, inView, viewerOpen]);

  useEffect(() => {
    const node = regionRef.current;
    if (primed) return undefined;
    if (!node || typeof IntersectionObserver === "undefined") {
      setPrimed(true);
      return undefined;
    }
    let cancelIdle = () => {};
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        cancelIdle = whenIdle(() => setPrimed(true));
      },
      { rootMargin: "400px 0px" }
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      cancelIdle();
    };
  }, [primed]);

  useEffect(() => {
    const node = regionRef.current;
    if (!playing || !node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio < 0.25) setPlaying(false);
      },
      { threshold: [0, 0.25] }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [playing]);

  const onPointerDown = (event) => {
    suppressClick.current = false;
    swipe.current = event.pointerType === "mouse" ? null : { x: event.clientX, y: event.clientY };
  };

  const onPointerUp = (event) => {
    const start = swipe.current;
    swipe.current = null;
    if (!start) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy) * 1.5) {
      suppressClick.current = true;
      go(dx < 0 ? 1 : -1, "swipe");
    }
  };

  const onStageClick = (event) => {
    if (suppressClick.current) {
      suppressClick.current = false;
      event.preventDefault();
      return;
    }
    setPlaying(false);
    setViewerOpen(true);
    trackAction("open_gallery", `${project.id}:image`);
  };

  const onStageFocus = (event) => {
    if (playing && isKeyboardFocus(event.currentTarget)) setPlaying(false);
  };

  const altOf = (slide) => (wide ? slide.alt : slide.narrowAlt || slide.alt);

  const viewerItems = useMemo(
    () =>
      slides.map((slide) => ({
        type: "visual",
        alt: slide.alt,
        visual: {
          ...screensOnly(slide.layers, gallery.ratio),
          bare: true,
          alt: slide.alt,
        },
      })),
    [slides, gallery]
  );

  return (
    <div
      ref={regionRef}
      className={`pf-gallery${playing ? " is-playing" : ""}`}
      role="region"
      aria-roledescription="carousel"
      aria-label={`${subject} screens`}
    >
      <div
        className="pf-visual pf-gallery__visual"
        style={{ "--pf-ratio": gallery.ratio, "--pf-ratio-narrow": gallery.narrowRatio }}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          swipe.current = null;
        }}
      >
        <VisualBackdrop backdrop={gallery.backdrop} priority={priority} />
        <button
          type="button"
          className="pf-visual__link pf-gallery__link"
          aria-haspopup="dialog"
          aria-label={`Open images: ${project.title}`}
          draggable={false}
          onClick={onStageClick}
          onFocus={onStageFocus}
        >
          <span className="pf-visual__stage" role="img" aria-label={altOf(shown)}>
            {slides.map((slide, position) =>
              primed || position === current || slide.key === shown.key ? (
                <span
                  key={slide.key}
                  className={`pf-gallery__slide${slide.key === shown.key ? " is-active" : ""}`}
                >
                  {slide.layers.map((layer, layerIndex) => (
                    <VisualLayer
                      key={layerIndex}
                      layer={layer}
                      priority={priority && slide.key === 0}
                      motion={!reduced && slide.key === shown.key}
                      onError={() => onSlideError(slide.key)}
                      onLoad={() => markLoaded(slide.key, layerIndex)}
                    />
                  ))}
                </span>
              ) : null
            )}
          </span>
        </button>
      </div>
      <div className="pf-gallery__controls">
        <p className="pf-gallery__count">
          <span className="pf-visually-hidden">Screen </span>
          {current + 1}
          <span aria-hidden="true"> / </span>
          <span className="pf-visually-hidden"> of </span>
          {count}
        </p>
        <button
          type="button"
          className="pf-gallery__button"
          aria-label={`Previous screen: ${subject}`}
          onClick={() => go(-1, "previous")}
        >
          <ChevronLeft size={16} strokeWidth={1.75} aria-hidden="true" />
        </button>
        {!reduced && (
          <button
            type="button"
            className="pf-gallery__button"
            aria-label={`${playing ? "Pause" : "Play"} slideshow: ${subject}`}
            onClick={togglePlay}
          >
            {playing ? (
              <Pause size={16} strokeWidth={1.75} aria-hidden="true" />
            ) : (
              <Play size={16} strokeWidth={1.75} aria-hidden="true" />
            )}
          </button>
        )}
        <button
          type="button"
          className="pf-gallery__button"
          aria-label={`Next screen: ${subject}`}
          onClick={() => go(1, "next")}
        >
          <ChevronRight size={16} strokeWidth={1.75} aria-hidden="true" />
        </button>
      </div>
      <p className="pf-visually-hidden" aria-live="polite">
        {announcement}
      </p>
      <MediaLightbox
        open={viewerOpen}
        items={viewerItems}
        initialIndex={current}
        title={project.title}
        onClose={() => setViewerOpen(false)}
      />
    </div>
  );
}

function ProjectGallery({ project, priority = false }) {
  const { gallery } = project;
  const [broken, setBroken] = useState([]);
  const slides = gallery.slides
    .map((slide, key) => ({ ...slide, key }))
    .filter((slide) => !broken.includes(slide.key));

  const onSlideError = useCallback((key) => {
    setBroken((list) => (list.includes(key) ? list : [...list, key]));
  }, []);

  if (slides.length < 2) {
    const only = slides[0] || gallery.slides[0];
    return (
      <ShowcaseVisual
        project={project}
        priority={priority}
        visual={{
          ...only,
          ratio: gallery.ratio,
          narrowRatio: gallery.narrowRatio,
          backdrop: gallery.backdrop,
        }}
      />
    );
  }

  return (
    <GalleryStage
      project={project}
      gallery={gallery}
      slides={slides}
      priority={priority}
      onSlideError={onSlideError}
    />
  );
}

export default ProjectGallery;
